import AttachmentViewer from "components/organisms/AttachmentViewer";
import AppPaths from "constants/appPaths";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

/**
 * Direct-URL / hyperlink entry point for attachments (PRE-1516).
 *
 * Opens one or more attachments straight into a modal viewer from a sharable
 * link. Supported query parameters:
 *   ?key=<s3-key>&name=<filename>&type=<mime>[&index=<n>][&pii=true]
 * or a batch payload:
 *   ?files=<base64-encoded JSON array of {key,name,type}>[&index=<n>][&pii=true]
 *
 * Closing the viewer returns the user to the previous page (or the tenant home
 * as a fallback), keeping them inside the platform.
 */
const decodeFiles = (search) => {
    const params = new URLSearchParams(search);

    const batch = params.get("files");
    if (batch) {
        try {
            const bytes = Uint8Array.from(window.atob(batch), (c) => c.charCodeAt(0));
            const parsed = JSON.parse(new TextDecoder().decode(bytes));
            const list = Array.isArray(parsed) ? parsed : [parsed];
            return list.filter((f) => f && f.key);
        } catch (e) {
            // fall through to single-file handling
        }
    }

    const key = params.get("key");
    if (key) {
        return [{ key, name: params.get("name") || key.split("/").pop(), type: params.get("type") || "" }];
    }

    return [];
};

const AttachmentDirectView = (props) => {
    const history = useHistory();
    const location = useLocation();
    const params = useParams();
    const [visible, setVisible] = useState(true);

    const search = location?.search || "";
    const files = useMemo(() => decodeFiles(search), [search]);

    const query = useMemo(() => new URLSearchParams(search), [search]);
    const startIndex = Math.max(0, parseInt(query.get("index") || "0", 10) || 0);
    const piiProtected = query.get("pii") === "true";

    const handleClose = useCallback(() => {
        setVisible(false);
        if (history.length > 1) {
            history.goBack();
        } else {
            const tenant = (params?.tenant || props?.tenant || "").toString().toLowerCase();
            history.push(tenant ? AppPaths.TENANT_HOME.replace(":tenant", tenant) : AppPaths.LOGIN);
        }
    }, [history, params, props]);

    if (files.length === 0) {
        return (
            <div className="flex h-64 items-center justify-center text-gray-500">
                No attachment specified in the link.
            </div>
        );
    }

    return (
        <AttachmentViewer
            visible={visible}
            files={files}
            currentIndex={startIndex}
            onClose={handleClose}
            mode="modal"
            showModeSwitcher={false}
            piiProtected={piiProtected}
        />
    );
};

export default AttachmentDirectView;
